import React, { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import Location from "./pages/Location";
import NotFound from "./pages/NotFound";
import ServicesPage from "./pages/ServicesPage";
import PricingPage from "./pages/PricingPage";
import CalculatorPage from "./pages/CalculatorPage";
import FAQPage from "./pages/FAQPage";
import AboutPage from "./pages/AboutPage";
import FurniturePage from "./pages/FurniturePage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import DCLaunchOffer from "./pages/DCLaunchOffer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import { VideosHomePage } from "./pages/videos/VideosHomePage";
import { VideosAllPage, VideosSearchPage } from "./pages/videos/VideosAllPage";
import { VideosCategoriesPage } from "./pages/videos/VideosCategoriesPage";
import { VideosCategoryDetailPage } from "./pages/videos/VideosCategoryDetailPage";
import { VideosPlaylistsPage } from "./pages/videos/VideosPlaylistsPage";
import { VideosCourseDetailPage } from "./pages/videos/VideosCourseDetailPage";
import { VideosCourseLessonPage } from "./pages/videos/VideosCourseLessonPage";
import { VideosWatchPage } from "./pages/videos/VideosWatchPage";
import { VideosAcademyPage } from "./pages/videos/VideosAcademyPage";
import { VideosHistoryPage } from "./pages/videos/VideosHistoryPage";
import ScrollProgress from "./components/ScrollProgress";

// Toast components are no longer loaded globally to improve initial load performance.
// They will be loaded dynamically by the forms when needed.

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const firstRender = React.useRef(true);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // Fire Meta Pixel PageView on client-side route changes (skip first load;
    // the base pixel code in index.html already fired it).
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("track", "PageView");
    }
  }, [pathname]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-transition-enter min-h-screen">
      <Routes location={location}>
        <Route path="/" element={<Index />} />
        {/* Section pages */}
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/furniture" element={<FurniturePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route
          path="/blog/arlington-airbnb-investment-guide"
          element={
            <Navigate
              to="/blog/hosting-your-arlington-home-on-airbnb"
              replace
            />
          }
        />
        <Route path="/dc-launch-offer" element={<DCLaunchOffer />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        {/* Location pages */}
        <Route
          path="/airbnb-management-alexandria"
          element={<Location city="alexandria" />}
        />
        <Route
          path="/airbnb-management-arlington"
          element={<Location city="arlington" />}
        />
        <Route
          path="/airbnb-management-fairfax"
          element={<Location city="fairfax" />}
        />
        <Route
          path="/airbnb-management-loudoun"
          element={<Location city="loudoun" />}
        />
        <Route
          path="/airbnb-management-prince-william"
          element={<Location city="prince-william" />}
        />
        <Route
          path="/airbnb-management-washington-dc"
          element={<Location city="washington-dc" />}
        />
        {/* Videos pages */}
        <Route path="/videos" element={<VideosHomePage />} />
        <Route path="/videos/all" element={<VideosAllPage />} />
        <Route path="/videos/shorts" element={<VideosAllPage shortsOnly />} />
        <Route path="/videos/categories" element={<VideosCategoriesPage />} />
        <Route path="/videos/c/:slug" element={<VideosCategoryDetailPage />} />
        <Route path="/videos/playlists" element={<VideosPlaylistsPage />} />
        <Route
          path="/videos/course/:slug"
          element={<VideosCourseDetailPage />}
        />
        <Route
          path="/videos/course/:slug/:videoId"
          element={<VideosCourseLessonPage />}
        />
        <Route path="/videos/watch/:id" element={<VideosWatchPage />} />
        <Route path="/videos/academy" element={<VideosAcademyPage />} />
        <Route path="/videos/history" element={<VideosHistoryPage />} />
        <Route path="/videos/search" element={<VideosSearchPage />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScrollProgress />
        <Suspense fallback={null}>
          {/* Global toasts removed for performance */}
        </Suspense>
        <BrowserRouter>
          <ScrollToTop />
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
